import { useState } from "react";
import { Check, Code2, Copy, Download, ExternalLink } from "lucide-react";
import { toast } from "sonner";
import { api, BACKEND, errMsg } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Label } from "@/components/ui/label";
import { Spinner } from "@/components/States";

function Snippet({ id, label, hint, code }) {
  const [done, setDone] = useState(false);
  const copy = async () => { try { await navigator.clipboard.writeText(code); setDone(true); setTimeout(() => setDone(false), 1500); } catch { toast.message(code); } };
  return (
    <div className="space-y-2">
      <div className="flex items-center justify-between gap-2"><Label>{label}</Label><Button type="button" size="sm" variant="outline" className="rounded-full" onClick={copy} data-testid={`kit-copy-${id}`}>{done ? <Check className="mr-1.5 h-3.5 w-3.5" /> : <Copy className="mr-1.5 h-3.5 w-3.5" />}{done ? "Copied" : "Copy"}</Button></div>
      <pre className="overflow-x-auto rounded-lg bg-secondary/60 p-3 font-mono text-[11px] leading-relaxed" data-testid={`kit-code-${id}`}>{code}</pre>
      <p className="text-xs text-muted-foreground">{hint}</p>
    </div>
  );
}

export function WebsiteKit({ slug, primary }) {
  const login = `${window.location.origin}/login?ws=${slug}`;
  const button = `<a href="${login}" style="display:inline-block;padding:12px 22px;border-radius:999px;background:${primary};color:#fff;text-decoration:none;font-family:system-ui,sans-serif;font-size:14px">Client &amp; Crew Login</a>`;
  const widget = `<div data-elm-lookbook></div>\n<script src="${BACKEND}/api/embed/lookbook.js?ws=${slug}" data-max="6"></script>`;
  return (
    <section className="space-y-5 rounded-xl border bg-card p-6" data-testid="website-kit-card">
      <div className="flex items-start justify-between gap-3">
        <div><Label className="flex items-center gap-2"><Code2 className="h-4 w-4" />Website kit</Label><p className="mt-1 text-xs text-muted-foreground">Paste these into your business website. The login button sends clients and crew to your branded sign-in; the lookbook widget updates itself whenever you mark a project complete.</p></div>
        <Button asChild type="button" size="sm" variant="ghost" className="rounded-full"><a href={`${BACKEND}/api/embed/demo?ws=${slug}`} target="_blank" rel="noreferrer" data-testid="kit-preview-link"><ExternalLink className="mr-1.5 h-3.5 w-3.5" />Preview</a></Button>
      </div>
      <Snippet id="login" label="Client & Crew login button" hint="Works anywhere HTML is allowed: Squarespace code block, WordPress HTML block, Wix embed." code={button} />
      <Snippet id="widget" label="Auto-updating lookbook widget" hint="Shows your latest completed projects as a grid. Change data-max to show more or fewer." code={widget} />
      <Snippet id="feed" label="JSON feed (for developers)" hint="Public read-only feed of completed projects and photos, if your web developer wants to build something custom." code={`${BACKEND}/api/public/feed/${slug}.json`} />
    </section>
  );
}

export function DataExport() {
  const [busy, setBusy] = useState(false);
  const run = async () => {
    setBusy(true);
    try { const r = await api.get("/workspace/export.zip", { responseType: "blob" }); const a = document.createElement("a"); a.href = URL.createObjectURL(r.data); a.download = "elm-export.zip"; a.click(); toast.success("Export ready"); }
    catch (e) { toast.error(errMsg(e)); } finally { setBusy(false); }
  };
  return (
    <section className="flex flex-wrap items-center justify-between gap-4 rounded-xl border bg-card p-6" data-testid="data-export-card">
      <div><Label className="flex items-center gap-2"><Download className="h-4 w-4" />Your data</Label><p className="mt-1 text-xs text-muted-foreground">Download everything your workspace owns as JSON (projects, schedules, estimates, expenses, hours, logs, wishlists, boards, people). It's yours.</p></div>
      <Button type="button" variant="outline" className="rounded-full" onClick={run} disabled={busy} data-testid="data-export-button">{busy ? <Spinner /> : <Download className="mr-2 h-4 w-4" />}Export everything</Button>
    </section>
  );
}
